import React, { useState, useEffect } from 'react';
import './AngerJournal.css';
import { trackJournalEntry, trackEngagement } from '../utils/analytics';

const AngerJournal = () => {
  const [entries, setEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString(),
    trigger: '',
    intensity: 5,
    thoughts: '',
    response: '',
    outcome: '',
    lessons: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [filterDate, setFilterDate] = useState('');

  useEffect(() => {
    const savedEntries = localStorage.getItem('angerJournalEntries');
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('angerJournalEntries', JSON.stringify(entries));
  }, [entries]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newEntry = {
      ...currentEntry,
      id: Date.now()
    };
    setEntries(prev => [newEntry, ...prev]);
    setCurrentEntry({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString(),
      trigger: '',
      intensity: 5,
      thoughts: '',
      response: '',
      outcome: '',
      lessons: ''
    });
    setShowForm(false);
    
    // Track journal entry creation
    trackJournalEntry('created');
    trackEngagement('journal', 'entry_created');
  };

  const deleteEntry = (id) => {
    setEntries(prev => prev.filter(entry => entry.id !== id));
    
    // Track journal entry deletion
    trackJournalEntry('deleted');
    trackEngagement('journal', 'entry_deleted');
  };

  const handleFilterChange = (e) => {
    const newFilterDate = e.target.value;
    setFilterDate(newFilterDate);
    
    // Track filter usage
    if (newFilterDate) {
      trackEngagement('journal', 'filtered');
    }
  };

  const clearFilter = () => {
    setFilterDate('');
    trackEngagement('journal', 'filter_cleared');
  };

  const toggleForm = () => {
    setShowForm(!showForm);
    if (!showForm) {
      trackEngagement('journal', 'form_opened');
    }
  };

  const filteredEntries = filterDate 
    ? entries.filter(entry => entry.date === filterDate)
    : entries;

  const averageIntensity = entries.length > 0 
    ? (entries.reduce((sum, entry) => sum + parseInt(entry.intensity), 0) / entries.length).toFixed(1)
    : 0;

  const commonTriggers = entries.reduce((acc, entry) => {
    const trigger = entry.trigger.toLowerCase();
    acc[trigger] = (acc[trigger] || 0) + 1;
    return acc;
  }, {});

  const topTriggers = Object.entries(commonTriggers)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3);

  return (
    <section className="anger-journal">
      <div className="journal-container">
        <div className="journal-header">
          <h2 className="journal-title">Anger Journal</h2>
          <p className="journal-subtitle">
            Track your anger episodes to identify patterns, triggers, and progress over time.
          </p>
        </div>

        <div className="journal-stats">
          <div className="stat-card">
            <h3>Total Entries</h3>
            <div className="stat-number">{entries.length}</div>
          </div>
          <div className="stat-card">
            <h3>Average Intensity</h3>
            <div className="stat-number">{averageIntensity}/10</div>
          </div>
          <div className="stat-card">
            <h3>Most Common Trigger</h3>
            <div className="stat-text">
              {topTriggers.length > 0 ? topTriggers[0][0] : 'None yet'}
            </div>
          </div>
        </div>

        <div className="journal-actions">
          <button 
            className="add-entry-btn"
            onClick={toggleForm}
          >
            {showForm ? 'Cancel' : '+ Add New Entry'}
          </button>
          <div className="filter-section">
            <label htmlFor="filterDate">Filter by Date:</label>
            <input
              type="date"
              id="filterDate"
              value={filterDate}
              onChange={handleFilterChange}
            />
            {filterDate && (
              <button 
                className="clear-filter"
                onClick={clearFilter}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <form className="journal-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date:</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={currentEntry.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="time">Time:</label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={currentEntry.time}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="trigger">What triggered your anger?</label>
              <textarea
                id="trigger"
                name="trigger"
                value={currentEntry.trigger}
                onChange={handleInputChange}
                placeholder="Describe what happened that made you angry..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="intensity">Anger Intensity (1-10):</label>
              <input
                type="range"
                id="intensity"
                name="intensity"
                min="1"
                max="10"
                value={currentEntry.intensity}
                onChange={handleInputChange}
                className="intensity-slider"
              />
              <div className="intensity-value">{currentEntry.intensity}/10</div>
            </div>

            <div className="form-group">
              <label htmlFor="thoughts">What were you thinking?</label>
              <textarea
                id="thoughts"
                name="thoughts"
                value={currentEntry.thoughts}
                onChange={handleInputChange}
                placeholder="What thoughts went through your mind?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="response">How did you respond?</label>
              <textarea
                id="response"
                name="response"
                value={currentEntry.response}
                onChange={handleInputChange}
                placeholder="What did you do or say?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="outcome">What was the outcome?</label>
              <textarea
                id="outcome"
                name="outcome"
                value={currentEntry.outcome}
                onChange={handleInputChange}
                placeholder="What happened as a result?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lessons">What did you learn?</label>
              <textarea
                id="lessons"
                name="lessons"
                value={currentEntry.lessons}
                onChange={handleInputChange}
                placeholder="What would you do differently next time?"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">Save Entry</button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="journal-entries">
          <h3>Your Entries</h3>
          {filteredEntries.length === 0 ? (
            <div className="no-entries">
              <p>No entries yet. Start by adding your first anger journal entry!</p>
            </div>
          ) : (
            <div className="entries-list">
              {filteredEntries.map(entry => (
                <div key={entry.id} className="entry-card">
                  <div className="entry-header">
                    <div className="entry-date">
                      {new Date(entry.date).toLocaleDateString()} at {entry.time}
                    </div>
                    <div className="entry-intensity">
                      Intensity: {entry.intensity}/10
                    </div>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteEntry(entry.id)}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="entry-content">
                    <div className="entry-section">
                      <strong>Trigger:</strong> {entry.trigger}
                    </div>
                    {entry.thoughts && (
                      <div className="entry-section">
                        <strong>Thoughts:</strong> {entry.thoughts}
                      </div>
                    )}
                    {entry.response && (
                      <div className="entry-section">
                        <strong>Response:</strong> {entry.response}
                      </div>
                    )}
                    {entry.outcome && (
                      <div className="entry-section">
                        <strong>Outcome:</strong> {entry.outcome}
                      </div>
                    )}
                    {entry.lessons && (
                      <div className="entry-section">
                        <strong>Lessons:</strong> {entry.lessons}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AngerJournal; 